import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Eye,
  EyeOff,
  LockKeyhole,
  Mail,
  ShieldCheck,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { AuthViewModel } from '../viewmodels/AuthViewModel';

export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const authViewModel = useMemo(() => new AuthViewModel(), []);

  useEffect(() => {
    authViewModel.checkAuthStatus();
    if (authViewModel.isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [authViewModel, navigate]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const success = await authViewModel.login({ email, password });

      if (success) {
        navigate('/dashboard');
      } else {
        const errorMessage = authViewModel.error || 'Login failed. Please check your credentials.';

        if (errorMessage.includes('HTTP 401') || errorMessage.includes('Unauthorized')) {
          setError('Invalid email or password. Please check your credentials and try again.');
        } else if (errorMessage.includes('HTTP 400') || errorMessage.includes('Bad Request')) {
          setError('Please enter a valid email address and password.');
        } else if (errorMessage.includes('HTTP 500') || errorMessage.includes('Internal Server Error')) {
          setError('Server error. Please try again later.');
        } else if (errorMessage.includes('Network') || errorMessage.includes('fetch')) {
          setError('Unable to connect to the server. Please check your internet connection.');
        } else {
          setError(errorMessage);
        }
      }
    } catch (loginError) {
      console.error('❌ Login error:', loginError);

      if (loginError instanceof Error) {
        if (loginError.message.includes('HTTP 401') || loginError.message.includes('Unauthorized')) {
          setError('Invalid email or password. Please check your credentials and try again.');
        } else if (loginError.message.includes('HTTP 400') || loginError.message.includes('Bad Request')) {
          setError('Please enter a valid email address and password.');
        } else if (loginError.message.includes('HTTP 500') || loginError.message.includes('Internal Server Error')) {
          setError('Server error. Please try again later.');
        } else if (loginError.message.includes('Network') || loginError.message.includes('fetch')) {
          setError('Unable to connect to the server. Please check your internet connection.');
        } else {
          setError('An unexpected error occurred. Please try again.');
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const accessHighlights = [
    'Role-aware access',
    'Live operational view',
    'Focused workflows',
  ];

  return (
    <div className="login-shell">
      <div className="login-grid" aria-hidden="true" />
      <div className="login-glow login-glow-one" aria-hidden="true" />
      <div className="login-glow login-glow-two" aria-hidden="true" />

      <aside className="login-brand-panel" aria-label="UniPrime admin console introduction">
        <div className="login-orbit login-orbit-large" aria-hidden="true">
          <span className="login-orbit-dot login-orbit-dot-one" />
        </div>
        <div className="login-orbit login-orbit-small" aria-hidden="true">
          <span className="login-orbit-dot login-orbit-dot-two" />
        </div>

        <div className="relative z-10 flex h-full max-w-2xl flex-col justify-between">
          <div className="inline-flex w-fit items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-blue-100 backdrop-blur-sm">
            <ShieldCheck className="h-4 w-4" aria-hidden="true" />
            UniPrime Operations
          </div>

          <div className="py-14">
            <p className="mb-5 text-xs font-bold uppercase tracking-[0.24em] text-cyan-300">
              Admin command center
            </p>
            <h1 className="max-w-xl text-4xl font-semibold leading-[1.08] tracking-[-0.035em] text-white xl:text-6xl">
              One secure workspace.
              <span className="mt-2 block text-blue-200">Every operation in view.</span>
            </h1>
            <p className="mt-6 max-w-lg text-base leading-7 text-slate-300 xl:text-lg">
              Manage providers, payments, requests and daily operations from one focused workspace built for decisive teams.
            </p>

            <div className="mt-10 grid max-w-xl gap-3 sm:grid-cols-3">
              {accessHighlights.map((item) => (
                <div key={item} className="login-proof">
                  <CheckCircle2 className="h-4 w-4 text-cyan-300" aria-hidden="true" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 text-sm text-slate-300">
            <span className="login-status-dot" aria-hidden="true" />
            Secure access gateway
          </div>
        </div>
      </aside>

      <main className="login-form-zone">
        <div className="w-full max-w-md">
          <Card className="login-card border-slate-200/80 bg-white">
            <CardHeader className="space-y-0 px-6 pb-5 pt-6 sm:px-8 sm:pt-8">
              <div className="mb-8 flex items-center justify-between gap-4">
                <img
                  src="/uniprime-new-logo-trans.png"
                  alt="UniPrime"
                  className="h-auto w-40 object-contain sm:w-44"
                />
                <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-blue-100 bg-blue-50 text-blue-700">
                  <LockKeyhole className="h-5 w-5" aria-hidden="true" />
                </div>
              </div>

              <div className="mb-3 inline-flex w-fit items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                <ShieldCheck className="h-3.5 w-3.5 text-blue-600" aria-hidden="true" />
                Protected admin access
              </div>
              <CardTitle className="text-3xl font-semibold tracking-[-0.025em] text-slate-950">
                Welcome back
              </CardTitle>
              <CardDescription className="mt-2 text-sm leading-6 text-slate-500">
                Sign in with your authorized UniPrime account.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6 px-6 pb-6 sm:px-8 sm:pb-8">
              {error && (
                <div
                  className="flex gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
                  role="alert"
                  aria-live="polite"
                >
                  <span
                    className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-red-100 font-bold text-red-600"
                    aria-hidden="true"
                  >
                    !
                  </span>
                  <p>{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-semibold text-slate-700">
                    Email address
                  </label>
                  <div className="relative">
                    <Mail
                      className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400"
                      aria-hidden="true"
                    />
                    <Input
                      id="email"
                      type="email"
                      autoComplete="username"
                      placeholder="name@company.com"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      disabled={isLoading}
                      required
                      className="h-12 rounded-xl border-slate-200 bg-slate-50/70 pl-11 pr-4 text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="password" className="block text-sm font-semibold text-slate-700">
                    Password
                  </label>
                  <div className="relative">
                    <LockKeyhole
                      className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400"
                      aria-hidden="true"
                    />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      disabled={isLoading}
                      required
                      className="h-12 rounded-xl border-slate-200 bg-slate-50/70 pl-11 pr-12 text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2.5 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50"
                      disabled={isLoading}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <EyeOff className="h-[18px] w-[18px]" aria-hidden="true" />
                      ) : (
                        <Eye className="h-[18px] w-[18px]" aria-hidden="true" />
                      )}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="group h-12 w-full rounded-xl bg-blue-600 text-base font-semibold text-white shadow-[0_12px_28px_-14px_rgba(37,99,235,0.9)] hover:bg-blue-700 hover:shadow-[0_16px_32px_-14px_rgba(37,99,235,0.9)] focus:ring-blue-500"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg
                        className="mr-3 h-5 w-5 animate-spin text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      Signing in...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Sign in securely
                      <ArrowRight
                        className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                        aria-hidden="true"
                      />
                    </span>
                  )}
                </Button>
              </form>

              <div className="flex items-center justify-center gap-2 border-t border-slate-100 pt-5 text-xs text-slate-500">
                <ShieldCheck className="h-4 w-4 text-slate-400" aria-hidden="true" />
                Access is limited to authorized personnel
              </div>
            </CardContent>
          </Card>

          <p className="mt-6 text-center text-xs text-slate-400 lg:text-slate-500">
            © 2026 by UniPrime LLC. All rights reserved.
          </p>
        </div>
      </main>
    </div>
  );
};
